/* ------------------------------------------------------------------ */
/* Weekly driver payslip — shared model + HTML builder                 */
/* Used for both "Save PDF" (print) and "Send Email Summary".          */
/* ------------------------------------------------------------------ */

import jsPDF from "jspdf";
import "jspdf-autotable";
import type { PayslipElement } from "@/lib/payslipConfig";
import { applyPayslipPlaceholders } from "@/lib/payslipConfig";
import { formatDateTime } from "@/lib/dates";
import { SUMMARY_COPY, type SummaryKind, type SummarySegment } from "@/lib/summaryKind";

export interface PayslipDeductionLine {
  label: string;
  amount: number; // positive = subtracted, negative = a credit/rebate
  // Reducing-balance charges only (src/lib/driverDeductions.ts). Saved into
  // driver_weekly_summaries.deduction_lines with the summary, so a sent
  // summary keeps showing what it showed even if the charge is edited later.
  detail?: string; // "Original $1,000.00 · Paid to date $50.00 · Remaining $950.00"
  originalAmount?: number;
  paidToDate?: number;
  remaining?: number;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface PayslipBrandingInput {
  companyName?: string;
  tagline?: string;
  logoUrl?: string;
  accentColor?: string;
  footerNote?: string;
  showDailyBreakdown?: boolean;
}

export interface PayslipData {
  driverName: string;
  weekStart: string;
  weekEnd: string;
  /** Optional line shown beside the period, e.g. "Commission 28 Sep - 30 Sep, Lease from 1 Oct". */
  periodNote?: string;
  /** Commission summary (default), lease summary, or the combined one for a changeover week. */
  summaryKind?: SummaryKind;
  /** Changeover week only: net take and driver share for each part of the week. */
  segments?: SummarySegment[];

  grossTurnover: number; // total turnover (GST inclusive)
  gstAmount: number;     // GST portion of gross
  netTake: number;       // gross - GST (ex-GST turnover)

  commission: number;    // commission worked out per payment type (ex-GST)
  driverPayment: number; // netTake - commission

  bonus: number;         // based on gross/ex-GST turnover

  deductionLines: PayslipDeductionLine[];
  totalDeductions: number;

  totalPayment: number;  // driverPayment + bonus - totalDeductions

  // optional payslip branding (Driver Payment Builder)
  branding?: PayslipBrandingInput;

  // optional daily breakdown table
  columns?: string[];
  dailyRows?: Array<{ date: string; jobs: number; values: number[]; total: number }>;
  totalsRow?: { jobs: number; values: number[]; total: number };

  // Ordered layout blocks from the Driver Payment Builder (logo, heading,
  // text, tagline, footer, spacer, the daily amounts table). When present,
  // these blocks -- in this order -- replace the fixed header/daily-table
  // section below so the payslip actually reflects what was built. When
  // absent (a config saved before this existed), the fixed layout below
  // runs exactly as it always has, using the branding fields directly.
  elements?: PayslipElement[];
}

const money = (n: number) =>
  `$${(Number(n || 0) < 0 ? '-' : '')}${Math.abs(Number(n || 0)).toFixed(2)}`;

export function buildPayslipHtml(d: PayslipData): string {
  const period = `${d.weekStart} to ${d.weekEnd}${d.periodNote ? ` — ${escapeHtml(d.periodNote)}` : ''}`; // HTML
  const b = d.branding || {};
  const accent = b.accentColor || '#16a34a';
  const companyName = b.companyName || 'Weekly Payment Summary';
  const showDaily = b.showDailyBreakdown !== false;

  const logoHtml = b.logoUrl
    ? `<img src="${b.logoUrl}" alt="Logo" style="max-height:56px;max-width:220px;object-fit:contain;margin-bottom:8px;" />`
    : '';
  const taglineHtml = b.tagline
    ? `<div class="sub" style="margin-top:2px;">${b.tagline}</div>`
    : '';
  const footerNoteHtml = b.footerNote
    ? `<div class="foot-note">${b.footerNote}</div>`
    : '';

  const dailyTableHtml =
    d.columns && d.dailyRows && d.totalsRow
      ? `
      <h3 class="section-title">Daily Breakdown</h3>
      <table class="grid">
        <thead>
          <tr>
            <th>Date</th><th>Jobs</th>
            ${d.columns.map((c) => `<th class="num">${c}</th>`).join('')}
            <th class="num">Total</th>
          </tr>
        </thead>
        <tbody>
          ${d.dailyRows
            .map(
              (r) => `
            <tr>
              <td>${r.date}</td><td>${r.jobs}</td>
              ${r.values.map((v) => `<td class="num">${money(v)}</td>`).join('')}
              <td class="num">${money(r.total)}</td>
            </tr>`,
            )
            .join('')}
          <tr class="totals">
            <td>TOTAL</td><td>${d.totalsRow.jobs}</td>
            ${d.totalsRow.values.map((v) => `<td class="num">${money(v)}</td>`).join('')}
            <td class="num">${money(d.totalsRow.total)}</td>
          </tr>
        </tbody>
      </table>`
      : '';

  // Legacy fixed layout, used only when no builder blocks are saved (a
  // config from before the Driver Payment Builder's blocks existed).
  const legacyHeaderHtml = `
  <div class="header">
    ${logoHtml}
    <h1>${companyName}</h1>
    <div class="sub">${d.driverName}</div>
    ${taglineHtml}
  </div>
  <div class="meta"><strong>Driver Name:</strong> ${escapeHtml(d.driverName)}<br><strong>Period:</strong> ${period}</div>
  ${showDaily ? dailyTableHtml : ''}`;

  const deductionRows =
    d.deductionLines.length > 0
      ? d.deductionLines
          .map(
            (l) =>
              `<div class="ps-row"><span class="ps-sub">${escapeHtml(l.label)}${l.detail ? `<br><span style="color:#888;font-size:11px;">${escapeHtml(l.detail)}</span>` : ''}</span><span class="num ${l.amount < 0 ? 'green' : 'red'}">${l.amount < 0 ? '+' : '-'}${money(Math.abs(l.amount))}</span></div>`,
          )
          .join('')
      : `<div class="ps-row"><span class="ps-sub">No deductions</span><span class="num">$0.00</span></div>`;

  // The Gross Turnover -> Total Payment card. Its own block type ('summary')
  // so it can be positioned anywhere in the builder; falls back to always
  // appearing right after the header/daily table when no blocks are set.
  const copy = SUMMARY_COPY[d.summaryKind ?? 'commission'];
  const isLease = d.summaryKind === 'lease';
  const segmentRowsHtml = (d.segments ?? [])
    .map(
      (s) =>
        `<div class="ps-row"><span class="ps-sub">${escapeHtml(s.label)} <span style="color:#888;font-size:11px;">(net take ${money(s.netTake)})</span></span><span class="num">${money(s.driverShare)}</span></div>`,
    )
    .join('');
  const summaryCardHtml = `
  <div class="payslip">
    <div class="ps-head" style="background:${accent};">${copy.cardTitle} — ${d.driverName}</div>
    <div class="ps-body">
      ${isLease
        ? `${d.commission > 0.005 ? `<div class="ps-row"><span>Total Collected</span><span class="num">${money(d.grossTurnover)}</span></div>
      <div class="ps-row"><span>${copy.shareLabel}</span><span class="num red">-${money(d.commission)}</span></div>` : ''}
      <div class="ps-row ps-strong"><span>${copy.paymentLabel}</span><span class="num">${money(d.driverPayment)}</span></div>`
        : `<div class="ps-row"><span>Gross Turnover</span><span class="num">${money(d.grossTurnover)}</span></div>
      <div class="ps-row"><span>Less GST</span><span class="num red">-${money(d.gstAmount)}</span></div>
      <div class="ps-row ps-strong"><span>Net Take</span><span class="num">${money(d.netTake)}</span></div>
      <div class="ps-divider"></div>
      <div class="ps-row"><span>${copy.shareLabel}</span><span class="num red">-${money(d.commission)}</span></div>
      <div class="ps-row ps-strong"><span>${copy.paymentLabel}</span><span class="num">${money(d.driverPayment)}</span></div>`}
      ${segmentRowsHtml}
      <div class="ps-divider"></div>
      ${copy.showBonus ? `<div class="ps-row"><span>Bonus <span style="color:#888;font-size:11px;">(based on gross)</span></span><span class="num green">+${money(d.bonus)}</span></div>
      <div class="ps-divider"></div>` : ''}
      <div class="ps-row ps-strong"><span>Deductions</span><span class="num">${d.totalDeductions !== 0 ? '-' : ''}${money(Math.abs(d.totalDeductions))}</span></div>
      ${deductionRows}
    </div>
    <div class="ps-grand"><span>Total Payment</span><span class="num">${money(d.totalPayment)}</span></div>
  </div>`;

  // Renders one Driver Payment Builder block. This is the SAME rendering the
  // real payslip uses -- what's built in the builder is what prints here.
  const ph = (text: string) =>
    applyPayslipPlaceholders(text, { driverName: d.driverName, weekStart: d.weekStart, weekEnd: d.weekEnd });

  const renderBlock = (el: PayslipElement): string => {
    const align = el.align === 'left' ? 'left' : el.align === 'right' ? 'right' : 'center';
    switch (el.type) {
      case 'logo':
        return el.value
          ? `<div style="text-align:${align};margin-bottom:8px;"><img src="${el.value}" alt="Logo" style="max-height:56px;max-width:220px;object-fit:contain;" /></div>`
          : '';
      case 'heading':
        return el.value ? `<h1 style="text-align:${align};margin:0 0 4px;">${ph(el.value)}</h1>` : '';
      case 'tagline':
        return el.value ? `<div class="sub" style="text-align:${align};margin-top:2px;">${ph(el.value)}</div>` : '';
      case 'text':
        return el.value ? `<div style="text-align:${align};margin:4px 0;">${ph(el.value)}</div>` : '';
      case 'footer':
        return el.value ? `<div class="foot-note" style="text-align:${align};">${ph(el.value)}</div>` : '';
      case 'spacer':
        return `<div style="height:16px;"></div>`;
      case 'daily_table':
        return dailyTableHtml;
      case 'summary':
        return summaryCardHtml;
      default:
        return '';
    }
  };

  const blocksHtml =
    d.elements && d.elements.length > 0
      ? `<div class="header">${d.elements.map(renderBlock).join('')}<div class="sub">${d.driverName}</div></div><div class="meta"><strong>Driver Name:</strong> ${escapeHtml(d.driverName)}<br><strong>Period:</strong> ${period}</div>`
      : legacyHeaderHtml;

  const hasSummaryBlock = !!(d.elements && d.elements.length > 0 && d.elements.some((e) => e.type === 'summary'));

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Payment Summary — ${d.driverName}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 28px; }
  .header { text-align: center; margin-bottom: 8px; }
  .header h1 { margin: 0; font-size: 22px; }
  .header .sub { color: #555; font-size: 13px; margin-top: 4px; }
  .meta { margin: 18px 0 8px; font-size: 13px; color: #333; }
  .section-title { font-size: 14px; margin: 22px 0 8px; border-bottom: 2px solid ${accent}; padding-bottom: 4px; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
  table.grid th, table.grid td { border: 1px solid #ddd; padding: 5px 6px; text-align: left; }
  table.grid th { background: #f3f3f3; }
  table.grid td.num, table.grid th.num { text-align: right; }
  table.grid tr.totals td { font-weight: 700; background: #fafafa; }

  .payslip { margin-top: 24px; border: 1px solid #ccc; border-radius: 8px; overflow: hidden; max-width: 460px; }
  .payslip .ps-head { background: #111; color: #fff; padding: 10px 16px; font-weight: 700; font-size: 14px; }
  .payslip .ps-body { padding: 12px 16px; font-size: 14px; }
  .ps-row { display: flex; justify-content: space-between; padding: 6px 0; }
  .ps-row .num { font-variant-numeric: tabular-nums; }
  .ps-sub { padding-left: 14px; color: #444; }
  .ps-divider { border-top: 1px solid #e2e2e2; margin: 6px 0; }
  .ps-strong { font-weight: 700; }
  .ps-grand { display:flex; justify-content: space-between; padding: 10px 16px; background: ${accent}1a; border-top: 2px solid ${accent}; font-weight: 800; font-size: 16px; }
  .green { color: ${accent}; }
  .red { color: #dc2626; }
  .foot { margin-top: 28px; text-align: center; color: #888; font-size: 11px; }
  .foot-note { margin-top: 18px; padding-top: 10px; border-top: 1px solid #e2e2e2; text-align: center; color: #555; font-size: 12px; }
  @media print { body { padding: 12px; } }
</style>
</head>
<body>
  ${blocksHtml}

  ${hasSummaryBlock ? '' : summaryCardHtml}

  ${d.elements && d.elements.length > 0 ? '' : footerNoteHtml}

  <div class="foot">Generated ${formatDateTime(new Date())}</div>
</body>
</html>`;
}

/* Open the payslip in a print window so the user can "Save as PDF". */
export function printPayslip(html: string): void {
  const w = window.open('', '_blank', 'width=820,height=1000');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  // give the new window a moment to render before invoking print
  setTimeout(() => {
    w.focus();
    w.print();
  }, 350);
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [22, 163, 74];
}

async function loadImageAsDataUrl(url: string): Promise<{ dataUrl: string; format: string; width: number; height: number } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const dims = await new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
    const mimeMatch = dataUrl.match(/^data:image\/(\w+);/);
    const format = mimeMatch ? mimeMatch[1].toUpperCase().replace('JPG', 'JPEG') : 'JPEG';
    return { dataUrl, format, ...dims };
  } catch {
    return null;
  }
}

type Align = 'left' | 'center' | 'right';

/**
 * Builds a real, downloadable/emailable PDF (as opposed to buildPayslipHtml's
 * print-window HTML) -- same jsPDF + autoTable approach as generateClaimPdf.ts.
 *
 * Renders the Driver Payment Builder's blocks (d.elements), in the order the
 * admin configured them, exactly like buildPayslipHtml's renderBlock does for
 * the print/email HTML -- the whole point of the builder is that what's built
 * there is what actually prints, so the PDF needs the same block-driven
 * layout, not a fixed one. Falls back to the old fixed layout only for a
 * config saved before the blocks feature existed (elements empty/absent).
 */
export async function buildPaymentSummaryPdf(d: PayslipData): Promise<jsPDF> {
  const b = d.branding || {};
  const accent = hexToRgb(b.accentColor || '#16a34a');
  const period = `${d.weekStart} to ${d.weekEnd}${d.periodNote ? ` — ${d.periodNote}` : ''}`; // PDF

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;

  const ph = (text: string) =>
    applyPayslipPlaceholders(text, { driverName: d.driverName, weekStart: d.weekStart, weekEnd: d.weekEnd });

  const xFor = (align: Align) => (align === 'center' ? pageWidth / 2 : align === 'right' ? pageWidth - marginX : marginX);

  const ensureSpace = (y: number, needed: number): number => {
    if (y + needed > 280) {
      doc.addPage();
      return 18;
    }
    return y;
  };

  const drawLogo = async (y: number, url: string, align: Align): Promise<number> => {
    const img = await loadImageAsDataUrl(url);
    if (!img || img.width === 0) return y;
    const w = 40;
    const h = (img.height / img.width) * w;
    y = ensureSpace(y, h);
    const x = align === 'center' ? (pageWidth - w) / 2 : align === 'right' ? pageWidth - marginX - w : marginX;
    doc.addImage(img.dataUrl, img.format, x, y, w, h);
    return y + h + 4;
  };

  const drawHeading = (y: number, text: string, align: Align): number => {
    y = ensureSpace(y, 8);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(text, xFor(align), y, { align });
    return y + 7;
  };

  const drawTagline = (y: number, text: string, align: Align): number => {
    y = ensureSpace(y, 6);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(text, xFor(align), y, { align });
    doc.setTextColor(0);
    return y + 5;
  };

  const drawText = (y: number, text: string, align: Align): number => {
    y = ensureSpace(y, 6);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(text, pageWidth - marginX * 2);
    doc.text(lines, xFor(align), y, { align });
    return y + lines.length * 5 + 2;
  };

  const drawFooterNote = (y: number, text: string, align: Align): number => {
    y = ensureSpace(y, 12);
    doc.setDrawColor(226);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(90);
    const lines = doc.splitTextToSize(text, pageWidth - marginX * 2);
    doc.text(lines, xFor(align), y, { align });
    doc.setTextColor(0);
    return y + lines.length * 4.5 + 4;
  };

  const drawDailyTable = (y: number): number => {
    if (!(d.columns && d.dailyRows && d.totalsRow)) return y;
    y = ensureSpace(y, 24);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Daily Breakdown', marginX, y);
    (doc as any).autoTable({
      head: [['Date', 'Jobs', ...d.columns, 'Total']],
      body: [
        ...d.dailyRows.map((r) => [r.date, String(r.jobs), ...r.values.map((v) => money(v)), money(r.total)]),
        ['TOTAL', String(d.totalsRow.jobs), ...d.totalsRow.values.map((v) => money(v)), money(d.totalsRow.total)],
      ],
      startY: y + 3,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [243, 243, 243], textColor: 0 },
      didParseCell: (data: any) => {
        if (data.row.index === d.dailyRows!.length) data.cell.styles.fontStyle = 'bold';
      },
      theme: 'grid',
    });
    return (doc as any).lastAutoTable.finalY + 10;
  };

  const drawSummaryCard = (y: number): number => {
    y = ensureSpace(y, 60);
    doc.setFillColor(...accent);
    doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    const pdfCopy = SUMMARY_COPY[d.summaryKind ?? 'commission'];
    doc.text(`${pdfCopy.cardTitle} — ${d.driverName}`, marginX + 2, y + 5.5);
    doc.setTextColor(0);
    y += 8;

    const strongRows = new Set(['Net Take', pdfCopy.paymentLabel, 'Total Payment']);
    const pdfIsLease = d.summaryKind === 'lease';
    const summaryRows: string[][] = [
      ...(pdfIsLease
        ? d.commission > 0.005
          ? [['Total Collected', money(d.grossTurnover)], [pdfCopy.shareLabel, `-${money(d.commission)}`]]
          : []
        : [
            ['Gross Turnover', money(d.grossTurnover)],
            ['Less GST', `-${money(d.gstAmount)}`],
            ['Net Take', money(d.netTake)],
            [pdfCopy.shareLabel, `-${money(d.commission)}`],
          ]),
      [pdfCopy.paymentLabel, money(d.driverPayment)],
      ...(d.segments ?? []).map((s) => [`      ${s.label} (net take ${money(s.netTake)})`, money(s.driverShare)]),
      ...(pdfCopy.showBonus ? [['Bonus (based on gross)', `+${money(d.bonus)}`]] : []),
      ['Deductions', `${d.totalDeductions !== 0 ? '-' : ''}${money(Math.abs(d.totalDeductions))}`],
      ...(d.deductionLines.length > 0
        ? d.deductionLines.flatMap((l) => [
            [`  ${l.label}`, `${l.amount < 0 ? '+' : '-'}${money(Math.abs(l.amount))}`],
            // Balance detail sits on its own smaller grey row (styled below).
            ...(l.detail ? [[`      ${l.detail}`, '']] : []),
          ])
        : [['  No deductions', money(0)]]),
      ['Total Payment', money(d.totalPayment)],
    ];

    (doc as any).autoTable({
      body: summaryRows,
      startY: y,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: { 1: { halign: 'right' } },
      theme: 'plain',
      didParseCell: (data: any) => {
        const label = summaryRows[data.row.index]?.[0];
        if (label && strongRows.has(label)) data.cell.styles.fontStyle = 'bold';
        if (label && label.startsWith('      Original')) {
          data.cell.styles.fontSize = 8;
          data.cell.styles.textColor = [110, 110, 110];
        }
        if (data.row.index === summaryRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 12;
        }
      },
    });
    return (doc as any).lastAutoTable.finalY + 6;
  };

  let y = 18;

  if (d.elements && d.elements.length > 0) {
    // Elements-driven: render each Driver Payment Builder block in order,
    // exactly matching renderBlock's switch in buildPayslipHtml above.
    for (const el of d.elements) {
      switch (el.type) {
        case 'logo':
          if (el.value) y = await drawLogo(y, el.value, el.align);
          break;
        case 'heading':
          if (el.value) y = drawHeading(y, ph(el.value), el.align);
          break;
        case 'tagline':
          if (el.value) y = drawTagline(y, ph(el.value), el.align);
          break;
        case 'text':
          if (el.value) y = drawText(y, ph(el.value), el.align);
          break;
        case 'footer':
          if (el.value) y = drawFooterNote(y, ph(el.value), el.align);
          break;
        case 'spacer':
          y += 6;
          break;
        case 'daily_table':
          // No showDailyBreakdown gate here -- matches renderBlock exactly:
          // if the admin dragged the table block in, it prints.
          y = drawDailyTable(y);
          break;
        case 'summary':
          y = drawSummaryCard(y);
          break;
      }
    }

    // Driver name + period, always appended after the blocks (mirrors
    // blocksHtml's `...<div class="sub">${driverName}</div>` then the
    // Period meta line, both outside the per-block switch).
    y = ensureSpace(y, 13);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(85);
    doc.text(d.driverName, pageWidth / 2, y, { align: 'center' });
    doc.setTextColor(0);
    y += 6;
    doc.setFontSize(10);
    doc.text(`Driver Name: ${d.driverName}`, marginX, y);
    y += 5;
    doc.text(`Period: ${period}`, marginX, y);
    y += 8;

    // Same fallback as buildPayslipHtml: if no 'summary' block was placed,
    // the summary card still always appears at the end.
    if (!d.elements.some((e) => e.type === 'summary')) y = drawSummaryCard(y);
  } else {
    // Legacy fixed layout, used only when no builder blocks are saved.
    const companyName = b.companyName || 'Weekly Payment Summary';
    const showDaily = b.showDailyBreakdown !== false;

    if (b.logoUrl) y = await drawLogo(y, b.logoUrl, 'center');
    y = drawHeading(y, companyName, 'center');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(d.driverName, pageWidth / 2, y, { align: 'center' });
    y += 5;
    if (b.tagline) y = drawTagline(y, b.tagline, 'center');
    doc.setFontSize(10);
    doc.text(`Driver Name: ${d.driverName}`, marginX, y);
    y += 5;
    doc.text(`Period: ${period}`, marginX, y);
    y += 8;

    if (showDaily) y = drawDailyTable(y);
    y = drawSummaryCard(y);
    if (b.footerNote) y = drawFooterNote(y, b.footerNote, 'center');
  }

  doc.setFontSize(8);
  doc.setTextColor(140);
  doc.text(`Generated ${formatDateTime(new Date())}`, pageWidth / 2, 287, { align: 'center' });

  return doc;
}
